import { api } from "@convex/_generated/api";
import { Blob, Button, Popover, PopoverContent, PopoverTrigger, Toast } from "@buddy/ui";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import type { ReactNode } from "react";
import { clock, shortAge } from "@/lib/time";
import {
  useClaudeAccount,
  useDesktop,
  useSetupState,
  useWorkerInfo,
  useWorkerStatus,
  type WorkerInfo,
  type WorkerStatus,
} from "./bridge";
import { useDesktopChrome } from "./chrome-context";
import { ClaudePlanText } from "./claude-plan";

// Paper 13d: the local worker in the title bar. Healthy reads as plain grey text and a
// count; only exceptions take colour (attention when the human must act, negative when
// the process fell over). The popover says what runs here and how to stop it.

/** Employees whose runs this computer holds, from Convex. Undefined until org and worker id are known. */
const useRunningHere = (info: WorkerInfo | undefined) => {
  const org = useDesktopChrome()?.org ?? null;
  return useQuery(
    api.desktop.runningHere,
    org === null || info === undefined ? "skip" : { orgId: org.id, workerId: info.workerId },
  );
};

type Tone = "quiet" | "attention" | "negative";

const toneClass: Record<Tone, string> = {
  quiet: "text-text-2",
  attention: "text-attention",
  negative: "text-negative",
};

const summary = (status: WorkerStatus): { label: string; tone: Tone } => {
  switch (status._tag) {
    case "running":
      return { label: "Employees running here", tone: "quiet" };
    case "starting":
      return { label: "Starting employees", tone: "quiet" };
    case "restarting":
      return { label: "Employees restarting", tone: "negative" };
    case "paused":
      return { label: "Employees paused here", tone: "quiet" };
    case "stopped":
      return { label: "Employees stopped here", tone: status.crashed === undefined ? "quiet" : "negative" };
    case "notConfigured":
      return { label: "This computer is not connected", tone: "attention" };
  }
};

export function WorkerControl() {
  const chrome = useDesktopChrome();
  const status = useWorkerStatus();
  const open = chrome?.workerOpen ?? false;
  const info = useWorkerInfo(`${status?._tag}:${open}`);
  const rows = useRunningHere(info);
  const navigate = useNavigate();
  // The popover lists this org's employees, so it waits for the signed-in frame.
  if (chrome === null || chrome.org === null || status === undefined) return null;
  const { label, tone } = summary(status);
  return (
    <Popover open={open} onOpenChange={chrome.setWorkerOpen}>
      <PopoverTrigger className="flex h-6 items-center gap-2 rounded-sm px-1.5 outline-none transition-colors duration-150 [-webkit-app-region:no-drag] hover:bg-surface-3 focus-visible:bg-surface-3">
        <span className={`text-[12px] leading-4 tracking-normal ${toneClass[tone]}`}>{label}</span>
        {status._tag === "running" ? <Chip>{rows === undefined || rows.length === 0 ? "Idle" : `${rows.length} active`}</Chip> : null}
      </PopoverTrigger>
      <PopoverContent width={360} sideOffset={6} className="[-webkit-app-region:no-drag]">
        <WorkerPanel
          status={status}
          info={info}
          rows={rows}
          orgName={chrome.org.name}
          onSettings={() => {
            chrome.setWorkerOpen(false);
            void navigate({ to: "/settings/desktop" });
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/** The "2 active" chip from 13a: a 20px control-coloured tile with the button ring. */
const Chip = ({ children }: { children: ReactNode }) => (
  <span className="flex h-5 items-center rounded-sm bg-control px-1.5 text-[12px] font-medium leading-4 tracking-normal text-text shadow-button">
    {children}
  </span>
);

type Rows = ReturnType<typeof useRunningHere>;

function WorkerPanel({
  status,
  info,
  rows,
  orgName,
  onSettings,
}: {
  status: WorkerStatus;
  info: WorkerInfo | undefined;
  rows: Rows;
  orgName: string;
  /** Opens Settings, Desktop app, where Terminal is switched on or off. */
  onSettings: () => void;
}) {
  const bridge = useDesktop();
  const [setup] = useSetupState();
  const [claude, chooseClaude] = useClaudeAccount(status._tag);
  if (bridge === undefined) return null;
  const act = (action: () => Promise<void>) => () =>
    void action().catch((cause: unknown) => console.warn("[desktop] worker action failed", cause));
  const note = statusNote(status, orgName);
  const primary =
    status._tag === "running" || status._tag === "starting"
      ? { label: "Pause all", run: bridge.worker.pause }
      : status._tag === "paused" || status._tag === "stopped"
        ? { label: "Resume", run: bridge.worker.resume }
        : status._tag === "restarting"
          ? { label: "Restart now", run: bridge.worker.restart }
          : { label: "Connect", run: async () => onSettings() };

  return (
    <>
      <div className="flex flex-col gap-0.5 px-4 pb-3 pt-4">
        <p className="text-[14px] font-medium leading-[18px] text-text">This computer</p>
        <p className="text-[12px] leading-4 tracking-normal text-text-2">{info?.computerName ?? " "}</p>
      </div>

      <div className="flex items-center gap-3 py-1.5 pl-4 pr-2.5 shadow-[inset_0_1px_0_var(--color-hairline)]">
        <p className="min-w-0 flex-1 py-1.5 text-[13px] leading-[18px]">
          <ClaudePlanText account={claude} />
        </p>
        <Button size="sm" variant="ghost" onClick={chooseClaude}>
          Change
        </Button>
      </div>

      {note === null ? null : (
        <p className="px-4 py-3 text-[13px] leading-[18px] shadow-[inset_0_1px_0_var(--color-hairline)]">
          <span className={toneClass[note.tone]}>{note.lead}</span>
          {note.rest === undefined ? null : <span className="text-text-2"> {note.rest}</span>}
        </p>
      )}

      {rows !== undefined && (rows.length > 0 || status._tag === "running") ? (
        <div className="flex flex-col px-4">
          {rows.length === 0 ? (
            <p className="flex h-11 items-center text-[13px] leading-4 text-text-2 shadow-[inset_0_1px_0_var(--color-hairline)]">
              No one is working here right now.
            </p>
          ) : (
            rows.map((row) => (
              <div
                key={row.runId}
                className="flex h-11 shrink-0 items-center gap-2.5 shadow-[inset_0_1px_0_var(--color-hairline)]"
              >
                <Blob hue={row.hue} face={row.face} size={24} />
                <p className="w-16 shrink-0 truncate text-[13px] font-medium leading-4 text-text">{row.name}</p>
                <p className="min-w-0 flex-1 truncate text-[13px] leading-4 text-text-2">{row.activity ?? "Working"}</p>
                <p className="w-7 shrink-0 text-right text-[12px] leading-4 tracking-normal text-accent-text">
                  {shortAge(row.startedAt)}
                </p>
              </div>
            ))
          )}
        </div>
      ) : null}

      <dl className="flex flex-col px-4 py-1.5 shadow-[inset_0_1px_0_var(--color-hairline)]">
        <Detail label="Browser">
          {info === undefined
            ? " "
            : info.browserVersion === null
              ? "Not installed"
              : `Chrome ${info.browserVersion}, ${setup?.browserProfile === false ? "no profile yet" : "profile ready"}`}
        </Detail>
        <Detail label="Terminal">{setup === undefined ? " " : setup.terminal ? "On" : "Off"}</Detail>
        {status._tag === "running" ? (
          <>
            <Detail label="Memory">{info === undefined || info.memoryBytes === null ? " " : `${Math.round(info.memoryBytes / 1024 ** 2)} MB`}</Detail>
            <Detail label="Since">{since(status.since)}</Detail>
          </>
        ) : null}
      </dl>

      <div className="flex items-center gap-2 px-4 pb-4 pt-3 shadow-[inset_0_1px_0_var(--color-hairline)]">
        {primary === null ? null : (
          <Button size="sm" onClick={act(primary.run)}>
            {primary.label}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={act(bridge.worker.openLogs)}>
          Open logs
        </Button>
        <Button size="sm" variant="ghost" onClick={onSettings}>
          Settings
        </Button>
      </div>
    </>
  );
}

const Detail = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex h-7 shrink-0 items-center gap-2.5 text-[13px] leading-4 text-text-2">
    <dt className="w-[98px] shrink-0">{label}</dt>
    <dd className="min-w-0 truncate">{children}</dd>
  </div>
);

/** "08:02 today", or with the date once it is not today. */
const since = (at: number) => {
  const then = new Date(at);
  const today = new Date().toDateString() === then.toDateString();
  return today ? `${clock(at)} today` : `${clock(at)}, ${then.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
};

const seconds = (ms: number) => Math.max(1, Math.round(ms / 1000));

/** The one line that explains a state that is not plain running. */
const statusNote = (status: WorkerStatus, orgName: string): { lead: string; rest?: string; tone: Tone } | null => {
  switch (status._tag) {
    case "running":
      return null;
    case "starting":
      return { lead: "Starting.", rest: "Employees pick up work here in a moment.", tone: "quiet" };
    case "restarting":
      return {
        lead: `The employee process stopped with code ${status.exitCode}.`,
        rest: `Restarting in ${seconds(status.delayMs)}s, attempt ${status.attempt + 1}.`,
        tone: "negative",
      };
    case "paused":
      return { lead: "Paused.", rest: "Employees do not run on this computer until you resume.", tone: "quiet" };
    case "stopped":
      return status.crashed === undefined
        ? { lead: "Stopped.", tone: "quiet" }
        : {
            lead: `The employee process kept stopping, last with code ${status.crashed.exitCode}.`,
            rest: `Buddy stopped retrying after ${status.crashed.attempts} tries. Resume to try again, or open the logs.`,
            tone: "negative",
          };
    case "notConfigured":
      return {
        lead: `Not connected to ${orgName} yet.`,
        rest: "Connect it in Settings, Desktop app, and T3 Code on this computer runs here.",
        tone: "attention",
      };
  }
};

/** Paper 13e: shown while the worker restarts after exiting on its own. */
export function CrashToast({ status }: { status: Extract<WorkerStatus, { _tag: "restarting" }> }) {
  const chrome = useDesktopChrome();
  const info = useWorkerInfo(status.attempt);
  const rows = useRunningHere(info) ?? [];
  const names = rows.map((row) => row.name);
  const resumes =
    names.length === 0
      ? ""
      : names.length === 1
        ? ` ${names[0]}'s task resumes from the last step.`
        : ` ${names.slice(0, -1).join(", ")} and ${names.at(-1)}'s tasks resume from the last step.`;
  return (
    <Toast
      tone="attention"
      title="Employee process stopped"
      description={`Restarting in ${seconds(status.delayMs)}s.${resumes}`}
      action={
        chrome === null ? undefined : (
          <Button size="sm" variant="ghost" onClick={() => chrome.setWorkerOpen(true)}>
            Details
          </Button>
        )
      }
    />
  );
}
